import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { FileCardLocal } from "@msuite/ui/file-upload";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useMemo } from "react";
import { useTransferCarrier } from "@/hooks/use-transfer-carrier";
import { useTransferCarrierLabel } from "@/hooks/use-transfer-carrier-label";
import type { TransferCarrierPanelBaseProps } from "../shared";

/**
 * The plate that goes on the trolley, shown as the PDF that comes out of the printer.
 *
 * The preview is the document itself rather than a re-drawn approximation of it, which is the
 * point: what is checked before the paper is cut — that the code scans, that the number is the one
 * painted on the device — has to be checked on the thing being printed. A card that laid the same
 * fields out in HTML would agree with the label right up to the first time the template moved.
 *
 * Nothing here reprints or replaces anything. The label is rendered per request from the carrier
 * row, so an edited name means a new preview on the next load and there is no filed copy to go
 * stale — see `Stock.Printable.generateTransferCarrierLabel`. The card therefore offers the preview
 * and the download and nothing else: there is no row to unlink and no stored object to delete.
 */
export function TransferCarrierLabel({ transferCarrierId }: TransferCarrierPanelBaseProps) {
	/** Context */
	const { t } = useTranslation();

	/** Query */
	const { transferCarrier } = useTransferCarrier(transferCarrierId);
	const { label, isError } = useTransferCarrierLabel(transferCarrierId);

	/** Computed */
	// The card takes a File where the route answers a blob, and the name given here is the one the
	// download lands under — so it carries the number painted on the device rather than its id.
	const file = useMemo(() => {
		if (!label) return null;
		const number = transferCarrier?.number ?? transferCarrierId;
		return new File([label], `transportwagen-${number}.pdf`, { type: "application/pdf" });
	}, [label, transferCarrier?.number, transferCarrierId]);

	/** Render */
	return (
		<ContentCard
			title={t("stock.transfer_carrier_label")}
			icon={icon("QrCode")}
			variant="plain"
		>
			{isError ? (
				<BlankSlate
					label={t("stock.transfer_carrier_label_failed")}
					Icon={icon("QrCode")}
				/>
			) : (
				<FileCardLocal
					file={file}
					variant="content-card"
					withDownload
					withRemove={false}
					width={null}
					height={null}
				/>
			)}
		</ContentCard>
	);
}
